import { useState, useMemo, useCallback, useEffect } from 'react';
import { StabilizerCode, Detector, Observable } from '../../types/stabilizer';
import {
  AnalysisResult,
  runFullAnalysis,
  CommutativityResult,
  ResolvedCircuit,
  ScheduleEntry,
} from '../../utils/stabilizerAnalysis';
import {
  InjectedError,
  PropagationResult,
  FlatGateEntry,
  buildFlatCircuit,
  propagateErrors,
  buildQEPG,
  pauliLabel,
  isNonTrivial,
  QEPG,
} from '../../utils/errorPropagation';

interface Props {
  code: StabilizerCode;
  onCodeChange: (code: StabilizerCode) => void;
  onFlippedStabilizersChange?: (ids: Set<string>) => void;
}

type Tab = 'commute' | 'measure' | 'schedule' | 'compile' | 'preview' | 'detobs' | 'qepg';

const TABS: { key: Tab; label: string }[] = [
  { key: 'commute', label: 'Commute' },
  { key: 'measure', label: 'Circuits' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'compile', label: 'Compile' },
  { key: 'preview', label: 'Preview' },
  { key: 'detobs', label: 'Det/Obs' },
  { key: 'qepg', label: 'QEPG' },
];

export default function AnalysisPanel({ code, onCodeChange, onFlippedStabilizersChange }: Props) {
  const [tab, setTab] = useState<Tab>('commute');
  const [compileTarget, setCompileTarget] = useState<'stim' | 'qiskit'>('stim');
  const [injectedErrors, setInjectedErrors] = useState<InjectedError[]>([]);

  const analysis = useMemo<AnalysisResult | null>(() => {
    if (code.qubits.length === 0) return null;
    try { return runFullAnalysis(code); } catch { return null; }
  }, [code]);

  // Flat circuit for error propagation
  const flatCircuit = useMemo<FlatGateEntry[]>(() => {
    if (!analysis) return [];
    return buildFlatCircuit(analysis.resolvedCircuits, analysis.schedule);
  }, [analysis]);

  // Detector/observable measurement indices
  const detectorMeasIndices = useMemo(() => (code.detectors ?? []).map((d) => d.measurementIndices), [code.detectors]);
  const observableMeasIndices = useMemo(() => (code.observables ?? []).map((o) => o.measurementIndices), [code.observables]);

  // Propagation result
  const propagation = useMemo<PropagationResult | null>(() => {
    if (injectedErrors.length === 0 || flatCircuit.length === 0) return null;
    return propagateErrors(flatCircuit, injectedErrors, detectorMeasIndices, observableMeasIndices);
  }, [flatCircuit, injectedErrors, detectorMeasIndices, observableMeasIndices]);

  // Notify parent of flipped stabilizer IDs
  useEffect(() => {
    if (!onFlippedStabilizersChange) return;
    if (!propagation || propagation.flippedMeasurements.length === 0) {
      onFlippedStabilizersChange(new Set());
      return;
    }
    // Map flipped measurement indices → stabilizer IDs via flatCircuit
    const flippedIds = new Set<string>();
    for (const mi of propagation.flippedMeasurements) {
      const gate = flatCircuit.find((g) => g.gate === 'M' && g.measurementIndex === mi);
      if (gate) {
        const stab = code.stabilizers[gate.stabIndex];
        if (stab) flippedIds.add(stab.id);
      }
    }
    onFlippedStabilizersChange(flippedIds);
  }, [propagation, flatCircuit, code.stabilizers, onFlippedStabilizersChange]);

  // QEPG
  const qepg = useMemo<QEPG | null>(() => {
    if (flatCircuit.length === 0) return null;
    const detNames = (code.detectors ?? []).map((d) => d.name);
    const obsNames = (code.observables ?? []).map((o) => o.name);
    return buildQEPG(flatCircuit, code.qubits.length, detectorMeasIndices, observableMeasIndices, detNames, obsNames);
  }, [flatCircuit, code.qubits.length, detectorMeasIndices, observableMeasIndices, code.detectors, code.observables]);

  // Count total measurements for detector/observable UI
  const totalMeasurements = useMemo(() => {
    return flatCircuit.filter((g) => g.gate === 'M').length;
  }, [flatCircuit]);

  if (code.qubits.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-600 text-sm p-4">
        Add qubits and stabilizers to run analysis
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* Tabs */}
      <div className="flex border-b border-gray-800 shrink-0 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-2 py-2 text-xs font-medium transition-colors border-b-2 whitespace-nowrap ${
              tab === t.key ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}>{t.label}</button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 text-sm">
        {!analysis ? (
          <div className="text-gray-500">Analysis unavailable</div>
        ) : (
          <>
            {tab === 'commute' && <CommutativityTab result={analysis.commutativity} />}
            {tab === 'measure' && <CircuitsTab circuits={analysis.resolvedCircuits} />}
            {tab === 'schedule' && <ScheduleTab schedule={analysis.schedule} stabilizers={code.stabilizers} />}
            {tab === 'compile' && (
              <CompileTab stimCode={analysis.stimCode} qiskitCode={analysis.qiskitCode}
                target={compileTarget} onTargetChange={setCompileTarget} />
            )}
            {tab === 'preview' && (
              <InteractiveCircuitPreview
                circuits={analysis.resolvedCircuits}
                schedule={analysis.schedule}
                nDataQubits={code.qubits.length}
                flatCircuit={flatCircuit}
                injectedErrors={injectedErrors}
                onInjectedErrorsChange={setInjectedErrors}
                propagation={propagation}
                detectors={code.detectors ?? []}
                observables={code.observables ?? []}
              />
            )}
            {tab === 'detobs' && (
              <DetectorObservableTab
                code={code}
                onCodeChange={onCodeChange}
                totalMeasurements={totalMeasurements}
                flatCircuit={flatCircuit}
              />
            )}
            {tab === 'qepg' && <QEPGTab qepg={qepg} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab components ──

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ok ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
      {ok ? '\u2713' : '\u2717'} {label}
    </span>
  );
}

function CommutativityTab({ result }: { result: CommutativityResult }) {
  return (
    <div className="space-y-3">
      <StatusBadge ok={result.allCommute} label={result.allCommute ? 'All stabilizers commute' : 'Commutativity violations found'} />
      {result.violations.length > 0 && (
        <div className="space-y-2 mt-2">
          <h4 className="text-xs text-gray-400 uppercase font-semibold">Violations</h4>
          {result.violations.map((v, i) => (
            <div key={i} className="bg-red-950/30 border border-red-900/50 rounded p-2 text-xs">
              <div className="flex gap-2 items-center mb-1">
                <span className={`font-bold ${v.stabA.type === 'X' ? 'text-purple-400' : 'text-blue-400'}`}>
                  {v.stabA.name || v.stabA.type + '-' + v.stabA.id.slice(-4)}
                </span>
                <span className="text-gray-600">vs</span>
                <span className={`font-bold ${v.stabB.type === 'X' ? 'text-purple-400' : 'text-blue-400'}`}>
                  {v.stabB.name || v.stabB.type + '-' + v.stabB.id.slice(-4)}
                </span>
              </div>
              <div className="text-gray-400">
                Shared qubits (odd overlap): <span className="font-mono text-white">[{v.sharedQubits.join(', ')}]</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {result.allCommute && (
        <p className="text-xs text-gray-400 mt-1">All X-Z stabilizer pairs share an even number of qubits.</p>
      )}
    </div>
  );
}

function CircuitsTab({ circuits }: { circuits: ResolvedCircuit[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-gray-500 mb-2">
        Read-only view. Edit measurement circuits in the left panel by selecting a stabilizer.
      </p>
      {circuits.map((rc, i) => {
        const isOpen = expanded === i;
        return (
          <div key={i} className="bg-gray-800/60 border border-gray-700/50 rounded">
            <button onClick={() => setExpanded(isOpen ? null : i)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-gray-800">
              <span className={`font-bold ${rc.stabilizer.type === 'X' ? 'text-purple-400' : 'text-blue-400'}`}>{rc.stabilizer.type}</span>
              <span className="text-gray-300 flex-1 truncate">{rc.stabilizer.name || `Stab-${rc.stabilizer.id.slice(-4)}`}</span>
              <span className="text-gray-500 text-[10px] font-mono">{rc.circuit.length} gates</span>
              <span className="text-gray-600">{isOpen ? '\u25B2' : '\u25BC'}</span>
            </button>
            {isOpen && (
              <div className="px-2 pb-2 border-t border-gray-700/30">
                <div className="text-[10px] text-gray-400 mt-1 mb-1 font-mono">
                  Data: [{rc.dataQubitIndices.join(', ')}] | Ancilla: q{rc.ancillaIndex}
                  {rc.stabilizer.circuit ? (
                    <span className="ml-2 text-yellow-500">(custom)</span>
                  ) : rc.stabilizer.measurementCircuit ? (
                    <span className="ml-2 text-yellow-500">(legacy custom)</span>
                  ) : (
                    <span className="ml-2 text-gray-600">(default)</span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {rc.circuit.map((g, gi) => (
                    <div key={gi} className="flex items-center gap-2 text-[10px] font-mono text-gray-300 bg-gray-900/40 rounded px-1.5 py-0.5">
                      <span className="text-gray-600 w-3">{gi + 1}</span>
                      <span className="font-bold text-gray-200 w-5">{g.gate}</span>
                      <span>{g.targets.map((t) => `q${t}`).join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScheduleTab({ schedule, stabilizers }: { schedule: ScheduleEntry[]; stabilizers: StabilizerCode['stabilizers'] }) {
  return (
    <div className="space-y-3">
      <p className="text-[10px] text-gray-500 mb-2">
        Read-only view. Reorder by dragging stabilizers in the left panel.
      </p>
      <div className="bg-gray-800 rounded px-2 py-1 text-xs inline-block">
        <span className="text-gray-400">Steps: </span><span className="font-mono font-bold">{schedule.length}</span>
      </div>
      <div className="space-y-1">
        {schedule.map((entry) => {
          const s = stabilizers[entry.stabIndex];
          if (!s) return null;
          return (
            <div key={entry.stabId} className="flex items-center gap-2 bg-gray-800/40 border border-gray-700/40 rounded px-2 py-1.5">
              <span className="text-[10px] text-gray-600 font-mono w-4">{entry.order + 1}</span>
              <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
              <span className={`font-bold text-xs ${s.type === 'X' ? 'text-purple-400' : 'text-blue-400'}`}>{s.type}</span>
              <span className="text-xs text-gray-300 flex-1 truncate">{s.name || `Stab-${s.id.slice(-4)}`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompileTab({ stimCode, qiskitCode, target, onTargetChange }: {
  stimCode: string; qiskitCode: string; target: 'stim' | 'qiskit'; onTargetChange: (t: 'stim' | 'qiskit') => void;
}) {
  const code = target === 'stim' ? stimCode : qiskitCode;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex bg-gray-800 rounded overflow-hidden">
          {(['stim', 'qiskit'] as const).map((t) => (
            <button key={t} onClick={() => onTargetChange(t)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${target === t ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}>
              {t === 'stim' ? 'Stim' : 'Qiskit'}
            </button>
          ))}
        </div>
        <button onClick={() => navigator.clipboard.writeText(code)}
          className="ml-auto px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 rounded text-gray-300 transition-colors">Copy</button>
      </div>
      <pre className="bg-gray-950 border border-gray-800 rounded p-2 text-[11px] font-mono text-gray-300 overflow-x-auto whitespace-pre leading-relaxed max-h-[calc(100vh-180px)] overflow-y-auto">
        {code}
      </pre>
    </div>
  );
}

// ── Detector / Observable management ──

function DetectorObservableTab({ code, onCodeChange, totalMeasurements, flatCircuit }: {
  code: StabilizerCode;
  onCodeChange: (c: StabilizerCode) => void;
  totalMeasurements: number;
  flatCircuit: FlatGateEntry[];
}) {
  const detectors = code.detectors ?? [];
  const observables = code.observables ?? [];
  const [addingDet, setAddingDet] = useState(false);
  const [addingObs, setAddingObs] = useState(false);
  const [detName, setDetName] = useState('');
  const [detIndices, setDetIndices] = useState('');
  const [obsName, setObsName] = useState('');
  const [obsIndices, setObsIndices] = useState('');

  // Measurement summary
  const measSummary = useMemo(() => {
    const mGates = flatCircuit.filter((g) => g.gate === 'M');
    return mGates.map((g, i) => ({
      index: i,
      qubit: g.targets[0],
      stabIndex: g.stabIndex,
    }));
  }, [flatCircuit]);

  const parseIndices = (input: string): number[] | null => {
    const nums = input.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).map(Number);
    if (nums.some(isNaN)) return null;
    if (nums.some((n) => n < 0 || n >= totalMeasurements)) return null;
    return nums;
  };

  const addDetector = () => {
    const indices = parseIndices(detIndices);
    if (!indices) return;
    const det: Detector = {
      id: `det_${Date.now()}`,
      name: detName || `D${detectors.length}`,
      measurementIndices: indices,
    };
    onCodeChange({ ...code, detectors: [...detectors, det] });
    setDetName(''); setDetIndices(''); setAddingDet(false);
  };

  const addObservable = () => {
    const indices = parseIndices(obsIndices);
    if (!indices) return;
    const obs: Observable = {
      id: `obs_${Date.now()}`,
      name: obsName || `O${observables.length}`,
      measurementIndices: indices,
    };
    onCodeChange({ ...code, observables: [...observables, obs] });
    setObsName(''); setObsIndices(''); setAddingObs(false);
  };

  const removeDetector = (id: string) => {
    onCodeChange({ ...code, detectors: detectors.filter((d) => d.id !== id) });
  };

  const removeObservable = (id: string) => {
    onCodeChange({ ...code, observables: observables.filter((o) => o.id !== id) });
  };

  const autoCreateDetectors = () => {
    // Auto-create one detector per measurement (each stabilizer measurement)
    const newDets: Detector[] = measSummary.map((_m, i) => ({
      id: `det_auto_${Date.now()}_${i}`,
      name: `D${i}`,
      measurementIndices: [i],
    }));
    onCodeChange({ ...code, detectors: newDets });
  };

  return (
    <div className="space-y-4">
      {/* Measurement record summary */}
      <div>
        <h4 className="text-xs text-gray-400 uppercase font-semibold mb-1">Measurement Records ({totalMeasurements})</h4>
        <div className="max-h-32 overflow-y-auto space-y-0.5">
          {measSummary.map((m) => {
            const stab = code.stabilizers[m.stabIndex];
            return (
              <div key={m.index} className="flex items-center gap-2 text-[10px] font-mono bg-gray-800/40 rounded px-1.5 py-0.5">
                <span className="text-gray-500 w-5">m{m.index}</span>
                <span className="text-gray-300">q{m.qubit}</span>
                {stab && (
                  <span className={`${stab.type === 'X' ? 'text-purple-400' : 'text-blue-400'}`}>
                    {stab.name || `${stab.type}-${stab.id.slice(-4)}`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detectors */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs text-gray-400 uppercase font-semibold">Detectors ({detectors.length})</h4>
          <button onClick={autoCreateDetectors}
            className="text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors">
            Auto-create
          </button>
        </div>
        <div className="space-y-1">
          {detectors.map((d) => (
            <div key={d.id} className="flex items-center gap-1.5 bg-gray-800/50 border border-gray-700/40 rounded px-2 py-1 text-xs">
              <span className="font-bold text-cyan-400 shrink-0">{d.name}</span>
              <span className="text-gray-500">=</span>
              <span className="text-gray-300 font-mono text-[10px] flex-1 truncate">
                {d.measurementIndices.map((i) => `m${i}`).join(' \u2295 ')}
              </span>
              <button onClick={() => removeDetector(d.id)}
                className="shrink-0 text-gray-500 hover:text-red-400 transition-colors text-xs">&times;</button>
            </div>
          ))}
        </div>
        {addingDet ? (
          <div className="mt-1 border border-gray-700 rounded p-2 space-y-1.5">
            <input value={detName} onChange={(e) => setDetName(e.target.value)} placeholder="Name (e.g. D0)"
              className="w-full px-1.5 py-1 rounded bg-gray-900 border border-gray-600 text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500" />
            <input value={detIndices} onChange={(e) => setDetIndices(e.target.value)}
              placeholder="Measurement indices (e.g. 0, 1)"
              className="w-full px-1.5 py-1 rounded bg-gray-900 border border-gray-600 text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              onKeyDown={(e) => { if (e.key === 'Enter') addDetector(); if (e.key === 'Escape') setAddingDet(false); }} />
            <div className="flex gap-1">
              <button onClick={addDetector} className="flex-1 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-[11px]">Add</button>
              <button onClick={() => setAddingDet(false)} className="px-2 py-1 rounded bg-gray-800 text-gray-400 text-[11px]">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingDet(true)}
            className="mt-1 w-full py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 text-[11px]">
            + Add Detector
          </button>
        )}
      </div>

      {/* Observables */}
      <div>
        <h4 className="text-xs text-gray-400 uppercase font-semibold mb-1">Observables ({observables.length})</h4>
        <div className="space-y-1">
          {observables.map((o) => (
            <div key={o.id} className="flex items-center gap-1.5 bg-gray-800/50 border border-gray-700/40 rounded px-2 py-1 text-xs">
              <span className="font-bold text-orange-400 shrink-0">{o.name}</span>
              <span className="text-gray-500">=</span>
              <span className="text-gray-300 font-mono text-[10px] flex-1 truncate">
                {o.measurementIndices.map((i) => `m${i}`).join(' \u2295 ')}
              </span>
              <button onClick={() => removeObservable(o.id)}
                className="shrink-0 text-gray-500 hover:text-red-400 transition-colors text-xs">&times;</button>
            </div>
          ))}
        </div>
        {addingObs ? (
          <div className="mt-1 border border-gray-700 rounded p-2 space-y-1.5">
            <input value={obsName} onChange={(e) => setObsName(e.target.value)} placeholder="Name (e.g. O0)"
              className="w-full px-1.5 py-1 rounded bg-gray-900 border border-gray-600 text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500" />
            <input value={obsIndices} onChange={(e) => setObsIndices(e.target.value)}
              placeholder="Measurement indices (e.g. 0, 2)"
              className="w-full px-1.5 py-1 rounded bg-gray-900 border border-gray-600 text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
              onKeyDown={(e) => { if (e.key === 'Enter') addObservable(); if (e.key === 'Escape') setAddingObs(false); }} />
            <div className="flex gap-1">
              <button onClick={addObservable} className="flex-1 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-[11px]">Add</button>
              <button onClick={() => setAddingObs(false)} className="px-2 py-1 rounded bg-gray-800 text-gray-400 text-[11px]">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingObs(true)}
            className="mt-1 w-full py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 text-[11px]">
            + Add Observable
          </button>
        )}
      </div>
    </div>
  );
}

// ── Interactive Circuit Preview with Error Injection ──
// Layout: each gate gets "injection slot" columns before it so errors
// don't overlap gates.  1-qubit gate → 1 slot + gate col.
//                        2-qubit gate → 2 slots + gate col.

const CELL = 32;
const WIRE_Y = CELL / 2;
const LABEL_W = 50;
const DET_H = 28;

const GATE_COLORS: Record<string, string> = {
  H: '#7c3aed', CX: '#2563eb', CZ: '#0891b2', M: '#dc2626', R: '#ca8a04',
};
const PAULI_COLORS: Record<string, string> = {
  X: '#ef4444', Z: '#a855f7', Y: '#22c55e', I: 'transparent',
};

interface GateVis {
  gate: string;
  targets: number[];
  logCol: number;       // logical column in flat circuit
  stabName: string;
  stabColor: string;
  measurementIndex?: number;
  gateVisCol: number;   // visual column of the gate
  firstSlotVisCol: number; // visual column of first injection slot
  nSlots: number;
}

function InteractiveCircuitPreview({
  circuits, schedule, nDataQubits, flatCircuit: _flatCircuit,
  injectedErrors, onInjectedErrorsChange,
  propagation, detectors, observables,
}: {
  circuits: ResolvedCircuit[];
  schedule: ScheduleEntry[];
  nDataQubits: number;
  flatCircuit: FlatGateEntry[];
  injectedErrors: InjectedError[];
  onInjectedErrorsChange: (errs: InjectedError[]) => void;
  propagation: PropagationResult | null;
  detectors: Detector[];
  observables: Observable[];
}) {
  const [dragPauli, setDragPauli] = useState<'X' | 'Y' | 'Z' | null>(null);

  const allQubitIndices = useMemo(() => {
    const set = new Set<number>();
    for (let i = 0; i < nDataQubits; i++) set.add(i);
    circuits.forEach((rc) => {
      set.add(rc.ancillaIndex);
      rc.circuit.forEach((g) => g.targets.forEach((t) => set.add(t)));
    });
    return [...set].sort((a, b) => a - b);
  }, [circuits, nDataQubits]);

  const qubitRowMap = useMemo(() => {
    const m = new Map<number, number>();
    allQubitIndices.forEach((qi, row) => m.set(qi, row));
    return m;
  }, [allQubitIndices]);

  const nWires = allQubitIndices.length;

  // Build visual layout: gates with injection slots
  const { gateVis, totalVisCols, sectionBreaks, logColToGateVis } = useMemo(() => {
    const arr: GateVis[] = [];
    const breaks: { visCol: number; label: string; color: string }[] = [];
    const lcMap = new Map<number, GateVis>();
    let visCol = 0;
    let measIdx = 0;
    let lastStab = '';

    let flatCol = 0;

    for (const entry of schedule) {
      const rc = circuits[entry.stabIndex];
      if (!rc) continue;
      const stabName = rc.stabilizer.name || `${rc.stabilizer.type}-${rc.stabilizer.id.slice(-4)}`;
      const stabColor = rc.stabilizer.color;

      if (lastStab && lastStab !== stabName) visCol += 1;
      breaks.push({ visCol, label: stabName, color: stabColor });
      lastStab = stabName;

      for (const g of rc.circuit) {
        const nSlots = g.targets.length > 1 ? 2 : 1;
        const firstSlotVisCol = visCol;
        visCol += nSlots;
        const gateVisCol = visCol;
        visCol++;

        const gv: GateVis = {
          gate: g.gate, targets: g.targets, logCol: flatCol,
          stabName, stabColor, nSlots, gateVisCol, firstSlotVisCol,
        };
        if (g.gate === 'M') { gv.measurementIndex = measIdx++; }
        arr.push(gv);
        lcMap.set(flatCol, gv);
        flatCol++;
      }
      flatCol++; // gap in flat circuit numbering
    }

    return { gateVis: arr, totalVisCols: visCol, sectionBreaks: breaks, logColToGateVis: lcMap };
  }, [circuits, schedule]);

  // Detector/observable count
  const nDetObs = detectors.length + observables.length;
  const classicalH = nDetObs > 0 ? nDetObs * DET_H + 16 : 0;
  const svgW = LABEL_W + totalVisCols * CELL + 20;
  const svgH = nWires * CELL + 24 + classicalH;

  // Helper: visual x for a visual column center
  const vx = (vc: number) => LABEL_W + vc * CELL + CELL / 2;

  // Propagation paths (horizontal segments + vertical branching arrows)
  const propPaths = useMemo(() => {
    if (!propagation) return null;
    const segments: { fromVC: number; toVC: number; qubitIndex: number; pauli: string }[] = [];
    const branches: { atVC: number; fromQI: number; toQI: number; pauli: string }[] = [];

    for (let si = 0; si < propagation.snapshots.length - 1; si++) {
      const cur = propagation.snapshots[si];
      const nxt = propagation.snapshots[si + 1];
      const curGV = logColToGateVis.get(cur.col);
      const nxtGV = logColToGateVis.get(nxt.col);
      if (!curGV || !nxtGV) continue;

      const fromVC = curGV.gateVisCol;
      const toVC = nxtGV.gateVisCol;

      for (const [qi, p] of nxt.state) {
        if (!isNonTrivial(p)) continue;
        segments.push({ fromVC, toVC, qubitIndex: qi, pauli: pauliLabel(p) });

        // Check if this qubit just gained error → branch arrow
        const hadBefore = cur.state.has(qi) && isNonTrivial(cur.state.get(qi)!);
        if (!hadBefore) {
          // find source qubit that had error before
          for (const [srcQI] of cur.state) {
            if (srcQI !== qi && isNonTrivial(cur.state.get(srcQI)!)) {
              branches.push({ atVC: curGV.gateVisCol, fromQI: srcQI, toQI: qi, pauli: pauliLabel(p) });
              break;
            }
          }
        }
      }
    }
    return { segments, branches };
  }, [propagation, logColToGateVis]);

  // Set of flipped measurement indices for highlighting
  const flippedMeasSet = useMemo(() => {
    if (!propagation) return new Set<number>();
    return new Set(propagation.flippedMeasurements);
  }, [propagation]);

  // Drag-and-drop: resolve drop position to circuit location
  const resolveDropPosition = useCallback((clientX: number, clientY: number, svgEl: SVGSVGElement) => {
    const rect = svgEl.getBoundingClientRect();
    const sx = (clientX - rect.left) * (svgW / rect.width);
    const sy = (clientY - rect.top) * (svgH / rect.height);

    const vc = Math.floor((sx - LABEL_W) / CELL);
    const row = Math.floor(sy / CELL);
    if (row < 0 || row >= nWires) return null;
    const qubitIndex = allQubitIndices[row];
    if (qubitIndex === undefined) return null;

    // Find which gate this visual column belongs to (as a slot)
    for (const gv of gateVis) {
      if (vc >= gv.firstSlotVisCol && vc < gv.gateVisCol) {
        return { qubitIndex, logCol: gv.logCol };
      }
    }
    return null;
  }, [svgW, svgH, nWires, allQubitIndices, gateVis]);

  const handleDragOver = useCallback((e: React.DragEvent<SVGSVGElement>) => {
    if (e.dataTransfer.types.includes('errorpauli')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<SVGSVGElement>) => {
    e.preventDefault();
    const pauli = e.dataTransfer.getData('errorpauli') as 'X' | 'Y' | 'Z';
    if (!pauli) return;
    const pos = resolveDropPosition(e.clientX, e.clientY, e.currentTarget);
    if (!pos) return;

    // Don't add duplicate at same position
    const exists = injectedErrors.some(
      (err) => err.qubitIndex === pos.qubitIndex && err.beforeGateCol === pos.logCol
    );
    if (exists) return;

    onInjectedErrorsChange([...injectedErrors, {
      id: `err_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      qubitIndex: pos.qubitIndex,
      beforeGateCol: pos.logCol,
      pauli,
    }]);
  }, [resolveDropPosition, injectedErrors, onInjectedErrorsChange]);

  // Click on existing error marker to remove it
  const handleErrorClick = useCallback((errId: string) => {
    onInjectedErrorsChange(injectedErrors.filter((e) => e.id !== errId));
  }, [injectedErrors, onInjectedErrorsChange]);

  // Palette drag start
  const handlePaletteDragStart = useCallback((e: React.DragEvent, pauli: 'X' | 'Y' | 'Z') => {
    e.dataTransfer.setData('errorpauli', pauli);
    e.dataTransfer.effectAllowed = 'copy';
    setDragPauli(pauli);
  }, []);

  const handlePaletteDragEnd = useCallback(() => { setDragPauli(null); }, []);

  if (gateVis.length === 0) {
    return <div className="text-gray-500 text-xs">No gates to preview.</div>;
  }

  return (
    <div className="space-y-2">
      {/* Draggable error palette with star icons matching circuit markers */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] text-gray-500 uppercase font-semibold">Drag to inject:</span>
        {(['X', 'Y', 'Z'] as const).map((p) => {
          const color = PAULI_COLORS[p];
          return (
            <div key={p} draggable
              onDragStart={(e) => handlePaletteDragStart(e, p)}
              onDragEnd={handlePaletteDragEnd}
              className={`flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none transition-all ${
                dragPauli === p ? 'opacity-50 scale-90' : 'hover:scale-105'
              }`}>
              <svg width={28} height={28} viewBox="-14 -14 28 28">
                <circle cx={0} cy={0} r={12} fill={color} opacity={0.15} />
                <polygon
                  points="0,-9 3,-3 9,-3 4,1 6,8 0,4 -6,8 -4,1 -9,-3 -3,-3"
                  fill={color} opacity={0.8} />
                <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={8} fill="white" fontWeight="bold" fontFamily="monospace">{p}</text>
              </svg>
              <span className="text-[10px] font-semibold" style={{ color }}>{p} error</span>
            </div>
          );
        })}
        {injectedErrors.length > 0 && (
          <button onClick={() => onInjectedErrorsChange([])}
            className="px-2 py-1 text-[10px] rounded bg-gray-800 text-gray-400 hover:text-red-400">
            Clear all ({injectedErrors.length})
          </button>
        )}
        <span className="text-[10px] text-gray-600 ml-1">
          Drag X/Y/Z onto empty slots in the circuit. Click an error to remove it.
        </span>
      </div>

      {/* Circuit SVG */}
      <div className="overflow-auto border border-gray-800 rounded bg-gray-950">
        <svg width={svgW} height={svgH}
          onDragOver={handleDragOver} onDrop={handleDrop}>

          {/* Qubit wire labels + wires */}
          {allQubitIndices.map((qi, row) => {
            const y = row * CELL + WIRE_Y;
            const isAnc = qi >= nDataQubits;
            return (
              <g key={`w-${qi}`}>
                <text x={LABEL_W - 4} y={y + 1} textAnchor="end" dominantBaseline="middle"
                  fontSize={9} fontFamily="monospace" fill={isAnc ? '#64748b' : '#9ca3af'}>
                  {isAnc ? `a${qi}` : `q${qi}`}
                </text>
                <line x1={LABEL_W} y1={y} x2={svgW - 10} y2={y}
                  stroke={isAnc ? '#334155' : '#374151'} strokeWidth={1}
                  strokeDasharray={isAnc ? '3 2' : undefined} />
              </g>
            );
          })}

          {/* Injection slot shading (visible when dragging) */}
          {dragPauli && gateVis.map((gv, i) => {
            const x1 = LABEL_W + gv.firstSlotVisCol * CELL;
            const w = gv.nSlots * CELL;
            return (
              <rect key={`slot-${i}`} x={x1} y={0} width={w} height={nWires * CELL}
                fill={PAULI_COLORS[dragPauli] || '#ffffff'} opacity={0.06} rx={2} />
            );
          })}

          {/* Section breaks */}
          {sectionBreaks.map((brk, i) => {
            const x = LABEL_W + brk.visCol * CELL;
            return (
              <g key={`brk-${i}`}>
                <line x1={x - 2} y1={0} x2={x - 2} y2={nWires * CELL}
                  stroke={brk.color} strokeWidth={1} opacity={0.3} strokeDasharray="2 2" />
                <rect x={x} y={0} width={brk.label.length * 5 + 8} height={12} rx={2}
                  fill={brk.color} opacity={0.2} />
                <text x={x + 4} y={9} fontSize={8} fill={brk.color} fontFamily="monospace">{brk.label}</text>
              </g>
            );
          })}

          {/* ── Propagation paths (dashed colored lines along wires) ── */}
          {propPaths && propPaths.segments.map((seg, i) => {
            const row = qubitRowMap.get(seg.qubitIndex);
            if (row === undefined) return null;
            const y = row * CELL + WIRE_Y;
            const x1 = vx(seg.fromVC);
            const x2 = vx(seg.toVC);
            const color = PAULI_COLORS[seg.pauli] || '#22c55e';
            return (
              <line key={`ps-${i}`} x1={x1} y1={y} x2={x2} y2={y}
                stroke={color} strokeWidth={3} opacity={0.35} strokeDasharray="6 3" />
            );
          })}

          {/* Propagation branch arrows (vertical, offset right of the gate) */}
          {propPaths && propPaths.branches.map((br, i) => {
            const r1 = qubitRowMap.get(br.fromQI);
            const r2 = qubitRowMap.get(br.toQI);
            if (r1 === undefined || r2 === undefined) return null;
            const x = vx(br.atVC) + 12; // offset right so it doesn't overlap the gate
            const y1 = r1 * CELL + WIRE_Y;
            const y2 = r2 * CELL + WIRE_Y;
            const color = PAULI_COLORS[br.pauli] || '#22c55e';
            const arrowDir = y2 > y1 ? 1 : -1;
            return (
              <g key={`pbr-${i}`}>
                <line x1={x} y1={y1} x2={x} y2={y2}
                  stroke={color} strokeWidth={2.5} opacity={0.7} strokeDasharray="4 2" />
                <polygon
                  points={`${x},${y2} ${x - 5},${y2 - arrowDir * 8} ${x + 5},${y2 - arrowDir * 8}`}
                  fill={color} opacity={0.8} />
              </g>
            );
          })}

          {/* ── Multi-qubit gate vertical connections ── */}
          {gateVis.filter((g) => g.targets.length > 1).map((gv, i) => {
            const rows = gv.targets.map((t) => qubitRowMap.get(t) ?? 0);
            const x = vx(gv.gateVisCol);
            const y1 = Math.min(...rows) * CELL + WIRE_Y;
            const y2 = Math.max(...rows) * CELL + WIRE_Y;
            return (
              <line key={`gc-${i}`} x1={x} y1={y1} x2={x} y2={y2}
                stroke={GATE_COLORS[gv.gate] || '#6b7280'} strokeWidth={1.5} />
            );
          })}

          {/* ── Gates ── */}
          {gateVis.map((gv, i) => {
            const x = vx(gv.gateVisCol);
            const color = GATE_COLORS[gv.gate] || '#6b7280';

            if (gv.targets.length === 2 && (gv.gate === 'CX' || gv.gate === 'CZ')) {
              const cr = qubitRowMap.get(gv.targets[0]) ?? 0;
              const tr = qubitRowMap.get(gv.targets[1]) ?? 0;
              const cy = cr * CELL + WIRE_Y;
              const ty = tr * CELL + WIRE_Y;
              return (
                <g key={`g-${i}`}>
                  <circle cx={x} cy={cy} r={3} fill={color} />
                  {gv.gate === 'CX' ? (
                    <>
                      <circle cx={x} cy={ty} r={7} fill="none" stroke={color} strokeWidth={1.5} />
                      <line x1={x - 5} y1={ty} x2={x + 5} y2={ty} stroke={color} strokeWidth={1.5} />
                      <line x1={x} y1={ty - 5} x2={x} y2={ty + 5} stroke={color} strokeWidth={1.5} />
                    </>
                  ) : (
                    <circle cx={x} cy={ty} r={3} fill={color} />
                  )}
                </g>
              );
            }

            const row = qubitRowMap.get(gv.targets[0]) ?? 0;
            const y = row * CELL + WIRE_Y;
            const hw = 12, hh = 10;
            const isFlipped = gv.gate === 'M' && gv.measurementIndex !== undefined
              && flippedMeasSet.has(gv.measurementIndex);
            const gateColor = isFlipped ? '#f59e0b' : color; // amber when flipped
            return (
              <g key={`g-${i}`}>
                {isFlipped && (
                  <rect x={x - hw - 3} y={y - hh - 3} width={(hw + 3) * 2} height={(hh + 3) * 2}
                    rx={4} fill="#f59e0b" opacity={0.25} />
                )}
                <rect x={x - hw} y={y - hh} width={hw * 2} height={hh * 2} rx={2}
                  fill={gateColor} stroke={isFlipped ? '#fbbf24' : 'none'} strokeWidth={isFlipped ? 2 : 0} />
                <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={8} fill={isFlipped ? '#000' : 'white'} fontWeight="bold" fontFamily="monospace">
                  {isFlipped ? 'M!' : gv.gate}
                </text>
                {gv.measurementIndex !== undefined && (
                  <text x={x} y={y + hh + 8} textAnchor="middle" fontSize={7}
                    fill={isFlipped ? '#fbbf24' : '#9ca3af'} fontWeight={isFlipped ? 'bold' : 'normal'}
                    fontFamily="monospace">
                    m{gv.measurementIndex}{isFlipped ? ' FLIPPED' : ''}
                  </text>
                )}
              </g>
            );
          })}

          {/* ── Injected error markers (click to remove) ── */}
          {injectedErrors.map((err) => {
            const row = qubitRowMap.get(err.qubitIndex);
            const gv = logColToGateVis.get(err.beforeGateCol);
            if (row === undefined || !gv) return null;
            const slotMidVC = gv.firstSlotVisCol + Math.floor(gv.nSlots / 2);
            const x = vx(slotMidVC);
            const y = row * CELL + WIRE_Y;
            const color = PAULI_COLORS[err.pauli];
            return (
              <g key={`err-${err.id}`} className="cursor-pointer" onClick={() => handleErrorClick(err.id)}>
                <circle cx={x} cy={y} r={12} fill={color} opacity={0.15} />
                <polygon
                  points={`${x},${y - 9} ${x + 3},${y - 3} ${x + 9},${y - 3} ${x + 4},${y + 1} ${x + 6},${y + 8} ${x},${y + 4} ${x - 6},${y + 8} ${x - 4},${y + 1} ${x - 9},${y - 3} ${x - 3},${y - 3}`}
                  fill={color} opacity={0.8} />
                <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={8} fill="white" fontWeight="bold" fontFamily="monospace">{err.pauli}</text>
                <title>Click to remove {err.pauli} error on q{err.qubitIndex}</title>
              </g>
            );
          })}

          {/* ── Classical wires: detectors & observables ── */}
          {nDetObs > 0 && (() => {
            const cTop = nWires * CELL + 8;
            return (
              <g>
                <line x1={0} y1={cTop - 4} x2={svgW} y2={cTop - 4}
                  stroke="#4b5563" strokeWidth={0.5} strokeDasharray="4 2" />

                {detectors.map((det, di) => {
                  const y = cTop + di * DET_H + DET_H / 2;
                  const isFlipped = propagation?.flippedDetectors.includes(di);
                  return (
                    <g key={`det-${di}`}>
                      <text x={LABEL_W - 4} y={y + 1} textAnchor="end" dominantBaseline="middle"
                        fontSize={8} fontFamily="monospace"
                        fill={isFlipped ? '#ef4444' : '#22d3ee'} fontWeight={isFlipped ? 'bold' : 'normal'}>
                        {det.name}
                      </text>
                      <line x1={LABEL_W} y1={y} x2={svgW - 10} y2={y}
                        stroke="#1e3a5f" strokeWidth={1} strokeDasharray="6 3" />
                      {det.measurementIndices.map((mi) => {
                        const mg = gateVis.find((g) => g.measurementIndex === mi);
                        if (!mg) return null;
                        const mx = vx(mg.gateVisCol);
                        const mRow = qubitRowMap.get(mg.targets[0]) ?? 0;
                        const my = mRow * CELL + WIRE_Y;
                        return (
                          <g key={`dc-${di}-${mi}`}>
                            <line x1={mx} y1={my + 10} x2={mx} y2={y - 4}
                              stroke={isFlipped ? '#ef4444' : '#22d3ee'} strokeWidth={1} opacity={0.5}
                              strokeDasharray="2 2" />
                            <circle cx={mx} cy={y} r={3}
                              fill={isFlipped ? '#ef4444' : '#22d3ee'} opacity={0.7} />
                          </g>
                        );
                      })}
                      {isFlipped && (
                        <text x={svgW - 16} y={y + 1} textAnchor="end" dominantBaseline="middle"
                          fontSize={9} fill="#ef4444" fontWeight="bold" fontFamily="monospace">FLIP</text>
                      )}
                    </g>
                  );
                })}

                {observables.map((obs, oi) => {
                  const y = cTop + (detectors.length + oi) * DET_H + DET_H / 2;
                  const isFlipped = propagation?.flippedObservables.includes(oi);
                  return (
                    <g key={`obs-${oi}`}>
                      <text x={LABEL_W - 4} y={y + 1} textAnchor="end" dominantBaseline="middle"
                        fontSize={8} fontFamily="monospace"
                        fill={isFlipped ? '#ef4444' : '#f97316'} fontWeight={isFlipped ? 'bold' : 'normal'}>
                        {obs.name}
                      </text>
                      <line x1={LABEL_W} y1={y} x2={svgW - 10} y2={y}
                        stroke="#5c3d1e" strokeWidth={1} strokeDasharray="6 3" />
                      {obs.measurementIndices.map((mi) => {
                        const mg = gateVis.find((g) => g.measurementIndex === mi);
                        if (!mg) return null;
                        const mx = vx(mg.gateVisCol);
                        const mRow = qubitRowMap.get(mg.targets[0]) ?? 0;
                        const my = mRow * CELL + WIRE_Y;
                        return (
                          <g key={`oc-${oi}-${mi}`}>
                            <line x1={mx} y1={my + 10} x2={mx} y2={y - 4}
                              stroke={isFlipped ? '#ef4444' : '#f97316'} strokeWidth={1} opacity={0.5}
                              strokeDasharray="2 2" />
                            <circle cx={mx} cy={y} r={3}
                              fill={isFlipped ? '#ef4444' : '#f97316'} opacity={0.7} />
                          </g>
                        );
                      })}
                      {isFlipped && (
                        <text x={svgW - 16} y={y + 1} textAnchor="end" dominantBaseline="middle"
                          fontSize={9} fill="#ef4444" fontWeight="bold" fontFamily="monospace">FLIP</text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Propagation summary */}
      {propagation && (
        <div className="border border-gray-800 rounded bg-gray-950 p-2 space-y-2">
          <h4 className="text-xs text-gray-400 uppercase font-semibold">Error Propagation Results</h4>
          <div className="flex flex-wrap gap-2">
            <div className="bg-gray-800 rounded px-2 py-1 text-[10px]">
              <span className="text-gray-400">Injected: </span>
              <span className="font-mono text-white">{injectedErrors.length}</span>
            </div>
            <div className="bg-gray-800 rounded px-2 py-1 text-[10px]">
              <span className="text-gray-400">Flipped measurements: </span>
              <span className="font-mono text-white">
                {propagation.flippedMeasurements.length > 0
                  ? propagation.flippedMeasurements.map((m) => `m${m}`).join(', ') : 'none'}
              </span>
            </div>
          </div>
          {detectors.length > 0 && (
            <div className="text-[10px]">
              <span className="text-gray-400">Detectors: </span>
              {propagation.flippedDetectors.length === 0
                ? <span className="text-green-400">None flipped</span>
                : propagation.flippedDetectors.map((di) => (
                    <span key={di} className="inline-block bg-red-900/40 text-red-400 rounded px-1.5 py-0.5 font-mono mr-1">
                      {detectors[di]?.name ?? `D${di}`}
                    </span>
                  ))}
            </div>
          )}
          {observables.length > 0 && (
            <div className="text-[10px]">
              <span className="text-gray-400">Observables: </span>
              {propagation.flippedObservables.length === 0
                ? <span className="text-green-400">None flipped</span>
                : propagation.flippedObservables.map((oi) => (
                    <span key={oi} className="inline-block bg-red-900/40 text-red-400 rounded px-1.5 py-0.5 font-mono mr-1">
                      {observables[oi]?.name ?? `O${oi}`}
                    </span>
                  ))}
            </div>
          )}
          <div className="text-[10px]">
            <span className="text-gray-400">Residual errors: </span>
            {propagation.finalState.size === 0
              ? <span className="text-green-400">None</span>
              : Array.from(propagation.finalState.entries()).map(([qi, p]) => {
                  if (!isNonTrivial(p)) return null;
                  const lbl = pauliLabel(p);
                  return (
                    <span key={qi} className="inline-block rounded px-1.5 py-0.5 font-mono mr-1"
                      style={{ backgroundColor: PAULI_COLORS[lbl] + '33', color: PAULI_COLORS[lbl] }}>
                      {lbl}{qi}
                    </span>
                  );
                })}
          </div>
        </div>
      )}

      <p className="text-[10px] text-gray-600">
        Drag X/Y/Z error gates from the palette above and drop onto the circuit slots. Click an injected error to remove it.
      </p>
    </div>
  );
}

// ── QEPG (Quantum Error Propagation Graph) ──

function QEPGTab({ qepg }: { qepg: QEPG | null }) {
  if (!qepg) {
    return <div className="text-gray-500 text-xs">Add stabilizers and define detectors/observables to generate QEPG.</div>;
  }

  if (qepg.edges.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-gray-500 text-xs">No error propagation edges found. Make sure detectors and observables are defined in the Det/Obs tab.</p>
        <p className="text-[10px] text-gray-600">The QEPG traces how single-qubit X/Z/Y errors on each data qubit propagate to flip detectors and observables.</p>
      </div>
    );
  }

  // Layout: data qubits on left, detectors/observables on right
  const dataNodes = qepg.nodes.filter((n) => n.type === 'data');
  const detNodes = qepg.nodes.filter((n) => n.type === 'detector');
  const obsNodes = qepg.nodes.filter((n) => n.type === 'observable');
  const rightNodes = [...detNodes, ...obsNodes];

  const nodeW = 48, nodeH = 24;
  const leftX = 30, rightX = 280;
  const gapY = 36;
  const topPad = 20;

  const leftPositions = new Map<string, { x: number; y: number }>();
  dataNodes.forEach((n, i) => leftPositions.set(n.id, { x: leftX, y: topPad + i * gapY }));

  const rightPositions = new Map<string, { x: number; y: number }>();
  rightNodes.forEach((n, i) => rightPositions.set(n.id, { x: rightX, y: topPad + i * gapY }));

  const svgH = Math.max(dataNodes.length, rightNodes.length) * gapY + topPad * 2;
  const svgW = rightX + nodeW + 30;

  const EDGE_COLORS: Record<string, string> = { X: '#ef4444', Z: '#3b82f6', Y: '#eab308' };

  // Group edges to avoid overlapping
  const edgesByPair = new Map<string, typeof qepg.edges>();
  for (const e of qepg.edges) {
    const key = `${e.from}-${e.to}`;
    const list = edgesByPair.get(key) ?? [];
    list.push(e);
    edgesByPair.set(key, list);
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-400">
        Quantum Error Propagation Graph: data qubits (left) → detectors/observables (right).
        Edge color = error type (
        <span className="text-red-400">X</span>,{' '}
        <span className="text-blue-400">Z</span>,{' '}
        <span className="text-yellow-400">Y</span>).
      </div>

      <div className="overflow-auto border border-gray-800 rounded bg-gray-950">
        <svg width={svgW} height={svgH}>
          {/* Edges */}
          {Array.from(edgesByPair.entries()).map(([key, edges]) => {
            return edges.map((e, ei) => {
              const from = leftPositions.get(e.from);
              const to = rightPositions.get(e.to);
              if (!from || !to) return null;
              const offset = (ei - (edges.length - 1) / 2) * 4;
              return (
                <line key={`${key}-${ei}`}
                  x1={from.x + nodeW} y1={from.y + nodeH / 2 + offset}
                  x2={to.x} y2={to.y + nodeH / 2 + offset}
                  stroke={EDGE_COLORS[e.pauliType] || '#6b7280'}
                  strokeWidth={1.5} opacity={0.6}
                  markerEnd="none" />
              );
            });
          })}

          {/* Edge arrowheads using small triangles */}
          {Array.from(edgesByPair.entries()).map(([key, edges]) => {
            return edges.map((e, ei) => {
              const from = leftPositions.get(e.from);
              const to = rightPositions.get(e.to);
              if (!from || !to) return null;
              const offset = (ei - (edges.length - 1) / 2) * 4;
              const tx = to.x - 2;
              const ty = to.y + nodeH / 2 + offset;
              return (
                <polygon key={`arrow-${key}-${ei}`}
                  points={`${tx},${ty} ${tx - 6},${ty - 3} ${tx - 6},${ty + 3}`}
                  fill={EDGE_COLORS[e.pauliType] || '#6b7280'} opacity={0.6} />
              );
            });
          })}

          {/* Data qubit nodes */}
          {dataNodes.map((n) => {
            const pos = leftPositions.get(n.id)!;
            return (
              <g key={n.id}>
                <rect x={pos.x} y={pos.y} width={nodeW} height={nodeH} rx={4}
                  fill="#1e293b" stroke="#475569" strokeWidth={1} />
                <text x={pos.x + nodeW / 2} y={pos.y + nodeH / 2 + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={9} fill="#e2e8f0" fontFamily="monospace" fontWeight="bold">{n.label}</text>
              </g>
            );
          })}

          {/* Detector/Observable nodes */}
          {rightNodes.map((n) => {
            const pos = rightPositions.get(n.id)!;
            const isDet = n.type === 'detector';
            return (
              <g key={n.id}>
                <rect x={pos.x} y={pos.y} width={nodeW} height={nodeH} rx={4}
                  fill={isDet ? '#0c2d48' : '#3d1e00'} stroke={isDet ? '#22d3ee' : '#f97316'} strokeWidth={1.5} />
                <text x={pos.x + nodeW / 2} y={pos.y + nodeH / 2 + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={9} fill={isDet ? '#22d3ee' : '#f97316'} fontFamily="monospace" fontWeight="bold">{n.label}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Edge list table */}
      {qepg.edges.length > 0 && (
        <div className="mt-2">
          <h4 className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Propagation Edges ({qepg.edges.length})</h4>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {qepg.edges.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] font-mono bg-gray-800/40 rounded px-1.5 py-0.5">
                <span className="text-gray-300">{e.from}</span>
                <span style={{ color: EDGE_COLORS[e.pauliType] }} className="font-bold">{e.pauliType}</span>
                <span className="text-gray-500">→</span>
                <span className="text-gray-300">{e.to}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


